'use client';

import React from 'react';
import { Button, SlideOver, type ButtonAsButton } from '@ui';

interface GridLayoutWithAsideProps {
  /** Optional id for the grid aside container */
  gridAsideId?: string;
  /** Content rendered in the aside navigation (mobile SlideOver) */
  asideNavContent?: React.ReactNode;
  /** Content rendered in the aside (desktop and inside the mobile SlideOver) */
  asideContent?: React.ReactNode;
  /** Content rendered in the main section area */
  sectionContent?: React.ReactNode;
  /** Label for the SlideOver title (e.g. "Users") */
  asideNavLabel?: string;
  /** Label for the mobile toggle button that opens the aside */
  toggleButtonLabel?: string;
  /** Optional class name for the mobile toggle button */
  toggleButtonClass?: string;
  /**
   * Native `<button>` props accepted by `Button` (`ButtonAsButton` from `@ui`; full union is `ButtonProps`).
   * `onClick` runs before the aside opens; `className` and `label` merge with `toggleButtonClass` / `toggleButtonLabel`.
   */
  toggleButtonProps?: ButtonAsButton;
  /** Optional id for the SlideOver panel */
  slideOverId?: string;
  /**
   * On viewports 992px and wider, a control collapses `aside-desktop` so
   * `grid-aside-main` fills the container. Mobile still uses the slide-over.
   */
  collapsible?: boolean;
  /** Desktop label while the aside is open. */
  collapseButtonLabel?: string;
  /** Desktop label while the aside is collapsed. */
  expandButtonLabel?: string;
}

const GridLayoutWithAside = ({
  gridAsideId,
  asideNavContent,
  asideContent,
  sectionContent,
  asideNavLabel = 'Side Panel',
  toggleButtonLabel = 'Show panel',
  toggleButtonClass = 'btn-ghost',
  slideOverId = 'aside-slideover',
  toggleButtonProps,
  collapsible = false,
  collapseButtonLabel = 'Hide Sidebar',
  expandButtonLabel = 'Show Sidebar',
}: GridLayoutWithAsideProps) => {
  const [slideOpen, setSlideOpen] = React.useState(false);
  const [asideCollapsed, setAsideCollapsed] = React.useState(false);

  const {
    onClick: toggleOnClick,
    className: togglePropsClassName,
    label: togglePropsLabel,
    ...toggleRest
  } = toggleButtonProps ?? {};

  const mergedToggleClassName = [toggleButtonClass, togglePropsClassName]
    .filter((value) => typeof value === 'string' && value.trim() !== '')
    .join(' ')
    .trim();

  return (
    <div
      className={`grid-with-aside${collapsible && asideCollapsed ? ' collapsed' : ''}`}
      id={gridAsideId}
    >
      <div className='aside-toggle-mobile'>
        <Button
          {...toggleRest}
          className={mergedToggleClassName || undefined}
          label={togglePropsLabel ?? toggleButtonLabel}
          onClick={(e) => {
            toggleOnClick?.(e);
            setSlideOpen(true);
          }}
          as='button'
        />
      </div>
      <aside className='aside-desktop'>{asideContent}</aside>
      <div className='grid-aside-main'>
        {collapsible ? (
          <div className='aside-toggle-desktop'>
            <Button
              className='btn-ghost'
              label={asideCollapsed ? expandButtonLabel : collapseButtonLabel}
              aria-expanded={!asideCollapsed}
              onClick={() => setAsideCollapsed((open) => !open)}
              as='button'
            />
          </div>
        ) : null}
        {sectionContent}
      </div>
      <SlideOver
        open={slideOpen}
        onClose={() => setSlideOpen(false)}
        title={asideNavLabel}
        id={slideOverId}
      >
        <aside className='aside-mobile'>{asideNavContent}</aside>
      </SlideOver>
    </div>
  );
};

export type { GridLayoutWithAsideProps };
export default GridLayoutWithAside;
